import { SitemapEntry } from '../sitemap.types';

// This catalog is intentionally isolated from normal feature and release work.
// Agents may edit it only during an explicitly requested sitemap update run.
export const ADMIN_OPERATIONS_SITEMAP_ENTRIES = [
	{
		id: 'review-missing-student-documents',
		title: 'Review missing student documents',
		guidance:
			'Find students with missing documents, then upload or waive the required paperwork or transcript.',
		href: '/app/dashboard/trackers/documents',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Dashboard Queues'],
		searchTerms: [
			'missing paperwork',
			'missing transcript',
			'upload document',
			'waive document',
		],
	},
	{
		id: 'review-pending-enrollment-approvals',
		title: 'Review pending enrollment approvals',
		guidance:
			'Review enrollment requests and approve, deny, or cancel eligible requests individually or in bulk.',
		href: '/app/dashboard/trackers/enrollment_approval',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Dashboard Queues'],
		searchTerms: [
			'student approval',
			'approve enrollment',
			'deny enrollment',
			'bulk approval',
		],
	},
	{
		id: 'review-pending-graduation-approvals',
		title: 'Review pending graduation approvals',
		guidance:
			'Open a student graduation review and inspect the student’s transcript before taking action.',
		href: '/app/dashboard/trackers/graduation_approval',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Dashboard Queues'],
		searchTerms: [
			'pending graduates',
			'graduation review',
			'graduate approval',
			'transcript',
		],
	},
	{
		id: 'review-academic-coach-students',
		title: 'Review Academic Coach students',
		guidance:
			'Open the Academic Coach tracker, optionally choose a staff member, and work with their assigned students.',
		href: '/app/trackers/ac',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Academic Coach'],
		searchTerms: [
			'AC tracker',
			'academic coach tracker',
			'assigned students',
			'work queue',
			'FOCUS queue',
		],
	},
	{
		id: 'review-career-counselor-students',
		title: 'Review Career Counselor students',
		guidance:
			'Open the Career Counselor tracker, optionally choose a staff member, and review their assigned students.',
		href: '/app/trackers/career_counselor',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Career Counselor'],
		searchTerms: [
			'career tracker',
			'career counselor tracker',
			'assigned students',
			'work queue',
		],
	},
	{
		id: 'review-enrollment-counselor-students',
		title: 'Review Enrollment Counselor students',
		guidance:
			'Open the Enrollment Counselor tracker, optionally choose a staff member, and review enrollment work for their students.',
		href: '/app/trackers/ec',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Enrollment Counselor'],
		searchTerms: [
			'EC tracker',
			'enrollment counselor tracker',
			'Salesforce',
			'assigned students',
			'work queue',
		],
	},
	{
		id: 'review-local-advocate-students',
		title: 'Review Local Advocate students',
		guidance:
			'Open the Local Advocate tracker, optionally choose a staff member, and work with their assigned students.',
		href: '/app/trackers/la',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Local Advocate'],
		searchTerms: [
			'LA tracker',
			'local advocate tracker',
			'assigned students',
			'work queue',
			'FOCUS queue',
		],
	},
	{
		id: 'review-grading-work',
		title: 'Review grading work',
		guidance:
			'Open the grader tracker, optionally choose a grader, and review assigned grading activity.',
		href: '/app/trackers/grader',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Grader'],
		searchTerms: [
			'grader tracker',
			'grading tracker',
			'assigned courses',
			'gradebook',
			'work queue',
		],
	},
	{
		id: 'review-teaching-work',
		title: 'Review teaching work',
		guidance:
			'Open the teacher tracker to review assigned students, registration dates, excused-assignment counts, course notes, progress reports, contacts, and related work.',
		href: '/app/trackers/teacher',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Teacher'],
		searchTerms: [
			'teacher tracker',
			'assigned staff',
			'Registration Created',
			'Excused Assignments',
			'View Teacher Insights',
			'course notes',
			'progress report',
			'gradebook',
			'work queue',
		],
	},
	{
		id: 'review-program-administrator-activity',
		title: 'Review program administrator activity',
		guidance:
			'Open the program-admin tracker, optionally choose a staff member, and review or export program activity.',
		href: '/app/trackers/program',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Program Administration'],
		searchTerms: [
			'program tracker',
			'admin tracker',
			'daily activity',
			'export counts',
			'work queue',
		],
	},
	{
		id: 'manage-transcript-work',
		title: 'Manage transcript work',
		guidance:
			'Open the transcript tracker to review transcript processing and staff activity.',
		href: '/app/trackers/transcript',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Transcripts'],
		searchTerms: [
			'transcript tracker',
			'uploaded transcript',
			'entered transcript',
			'verified transcript',
			'mapped transcript',
		],
	},
	{
		id: 'review-reentry-coordinator-work',
		title: 'Review re-entry coordinator work',
		guidance:
			'Open the re-entry coordinator tracker, optionally choose a staff member, and review assigned re-entry work.',
		href: '/app/trackers/reentry/coordinator',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Re-entry'],
		searchTerms: [
			'reentry coordinator',
			're-entry tracker',
			'returning student',
			'work queue',
		],
	},
	{
		id: 'review-reentry-coach-students',
		title: 'Review re-entry coach students',
		guidance:
			'Open the re-entry coach tracker, optionally choose a staff member, and review their assigned students.',
		href: '/app/trackers/reentry/coach',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Re-entry'],
		searchTerms: [
			'reentry coach',
			're-entry tracker',
			'returning student',
			'work queue',
		],
	},
	{
		id: 'review-graduation-coordinator-students',
		title: 'Review graduation coordinator students',
		guidance:
			'Open the graduation coordinator tracker, optionally choose a staff member, and review graduation-related student work.',
		href: '/app/trackers/grad/coordinator',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Graduation'],
		searchTerms: [
			'grad coordinator',
			'graduation tracker',
			'graduate students',
			'work queue',
		],
	},
	{
		id: 'review-credentialed-response-work',
		title: 'Review credentialed response work',
		guidance:
			'Open the credentialed responder tracker, optionally choose a staff member, and review assigned response work.',
		href: '/app/trackers/credentialed_responder',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Credentialed Responder'],
		searchTerms: [
			'credentialed responder',
			'response tracker',
			'assigned students',
			'work queue',
		],
	},
	{
		id: 'coordinate-student-testing',
		title: 'Coordinate student testing',
		guidance:
			'Open the testing coordinator tracker, optionally choose a staff member, and review student testing work.',
		href: '/app/trackers/testing/coordinator',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Testing'],
		searchTerms: [
			'testing coordinator',
			'testing tracker',
			'state test',
			'assigned students',
			'work queue',
		],
	},
	{
		id: 'review-student-progress-tracker',
		title: 'Review course progress across students',
		guidance:
			'Use the Student Progress tracker to review course progress reports and open a student’s activity workspace.',
		href: '/app/trackers/student_progress',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Trackers', 'Progress'],
		searchTerms: [
			'student progress tracker',
			'course progress',
			'progress report',
			'student activity',
			'work queue',
		],
	},
	{
		id: 'review-learning-coach-students',
		title: 'Review Learning Coach students',
		guidance:
			'Open the Learning Coach tracker, optionally choose a staff member, and manage assigned students and queues.',
		href: '/app/trackers/lc',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Learning Coach'],
		searchTerms: [
			'LC tracker',
			'learning coach tracker',
			'assigned students',
			'work queue',
			'FOCUS queue',
		],
	},
	{
		id: 'review-family-advocate-students',
		title: 'Review Family Advocate students',
		guidance:
			'Open the Family Advocate tracker to review assigned students, today’s and this week’s Meetup minutes, finals, class progress, insights, and queues.',
		href: '/app/trackers/fa',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Trackers', 'Family Advocate'],
		searchTerms: [
			'FA tracker',
			'family advocate tracker',
			'finals',
			'class progress',
			'family advocate meetup minutes',
			'today meetup minutes',
			'this week meetup minutes',
			'work queue',
			'FOCUS queue',
		],
	},
	{
		id: 'manage-staff-background-checks',
		title: 'Manage staff background checks',
		guidance: 'Review, add, edit, or remove staff background-check records.',
		href: '/app/trackers/staff_background_check',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Compliance'],
		searchTerms: [
			'background check tracker',
			'staff screening',
			'add background check',
			'remove background check',
		],
	},
	{
		id: 'review-registrar-pause-requests',
		title: 'Review student pause requests',
		guidance:
			'Review and process student pause requests in the Registrar tracker.',
		href: '/app/trackers/registrar/pause-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Registrar'],
		searchTerms: [
			'registrar',
			'pause request',
			'approve pause',
			'student leave',
		],
	},
	{
		id: 'review-registrar-withdrawal-requests',
		title: 'Review student withdrawal requests',
		guidance:
			'Review and process student withdrawal requests in the Registrar tracker.',
		href: '/app/trackers/registrar/withdraw-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Registrar'],
		searchTerms: [
			'registrar',
			'withdraw request',
			'withdrawal request',
			'approve withdrawal',
			'deactivate student',
		],
	},
	{
		id: 'review-registrar-graduation-requests',
		title: 'Review student graduation requests',
		guidance:
			'Review and process student graduation requests in the Registrar tracker.',
		href: '/app/trackers/registrar/graduation-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Registrar'],
		searchTerms: [
			'registrar',
			'graduation request',
			'approve graduation',
			'graduate student',
		],
	},
	{
		id: 'authorize-student-pause-requests',
		title: 'Authorize student pause requests',
		guidance:
			'Review pause-request details, expectations, and claimability before authorizing a pause.',
		href: '/app/trackers/exit_authorization/pause-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Authorization'],
		searchTerms: [
			'exit authorization',
			'pause authorization',
			'claimability',
			'student leave',
			'manager report',
		],
	},
	{
		id: 'authorize-student-withdrawal-requests',
		title: 'Authorize student withdrawal requests',
		guidance:
			'Review withdrawal-request details, expectations, and claimability before authorizing a withdrawal.',
		href: '/app/trackers/exit_authorization/withdraw-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Authorization'],
		searchTerms: [
			'exit authorization',
			'withdraw authorization',
			'withdrawal',
			'claimability',
			'manager report',
		],
	},
	{
		id: 'authorize-student-graduation-requests',
		title: 'Authorize student graduation requests',
		guidance:
			'Review graduation requests and supporting transcript information before authorizing graduation.',
		href: '/app/trackers/exit_authorization/graduation-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Authorization'],
		searchTerms: [
			'exit authorization',
			'graduation authorization',
			'graduate request',
			'unofficial transcript',
		],
	},
	{
		id: 'review-student-data-updates',
		title: 'Review pending student data updates',
		guidance:
			'Review pending student data-change requests and inspect submitted values or proof before approving or rejecting them.',
		href: '/app/trackers/student_update/pending',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Data Updates', 'Pending'],
		searchTerms: [
			'student update tracker',
			'pending update',
			'user update request',
			'profile change',
			'data change proof',
			'approve update',
			'reject update',
		],
	},
	{
		id: 'review-teacher-activity',
		title: 'Review teacher activity',
		guidance:
			'Use the Teacher Activity Board to review feedback and GradChat activity or configure included staff.',
		href: '/app/activity-boards/teacher',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Activity Boards'],
		searchTerms: [
			'teacher activity board',
			'teacher feedback',
			'teacher GradChat',
			'included teachers',
			'board administrators',
		],
	},
	{
		id: 'review-academic-coach-activity',
		title: 'Review Academic Coach activity',
		guidance:
			'Use the Academic Coach Activity Board to review student activity, contact, GradChat, and transcript metrics or configure included staff.',
		href: '/app/activity-boards/ac',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Activity Boards'],
		searchTerms: [
			'AC activity board',
			'academic coach metrics',
			'no classes',
			'no contact',
			'GradChat',
			'mapped transcript',
		],
	},
	{
		id: 'review-local-advocate-activity',
		title: 'Review Local Advocate activity',
		guidance:
			'Use the Local Advocate Activity Board to review contact and GradChat activity or configure included staff.',
		href: '/app/activity-boards/la',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Activity Boards'],
		searchTerms: [
			'LA activity board',
			'local advocate metrics',
			'contact activity',
			'GradChat',
			'included staff',
		],
	},
	{
		id: 'review-transcript-activity',
		title: 'Review transcript activity',
		guidance:
			'Use the Transcript Activity Board to review uploaded, entered, verified, mapped, and locked transcript work or configure included staff.',
		href: '/app/activity-boards/transcript',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Staff', 'Activity Boards'],
		searchTerms: [
			'transcript activity board',
			'uploaded transcripts',
			'verified transcripts',
			'mapped transcripts',
			'locked transcripts',
		],
	},
	{
		id: 'manage-current-student-paperwork',
		title: 'Manage paperwork across current students',
		guidance:
			'Browse current or expired paperwork, notify staff, download files, or expire or restore selected student paperwork.',
		href: '/app/paperwork-management/paperwork',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork'],
		searchTerms: [
			'paperwork manager',
			'current paperwork',
			'notify staff',
			'download paperwork',
			'expire paperwork',
			'restore expired paperwork',
			'restore student paperwork',
			'delete paperwork',
		],
	},
	{
		id: 'manage-enrollment-paperwork-requirements',
		title: 'Manage enrollment paperwork requirements',
		guidance:
			'Request or upload enrollment requirements and manage paperwork gates or bulk requests.',
		href: '/app/paperwork-management/requirements',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Enrollment'],
		searchTerms: [
			'enrollment requirement',
			'request paperwork',
			'upload requirement',
			'paperwork gate',
			'bulk send',
		],
	},
	{
		id: 'manage-reentry-paperwork-requirements',
		title: 'Manage re-entry paperwork requirements',
		guidance:
			'Request or upload re-entry requirements and manage paperwork gates or bulk requests.',
		href: '/app/paperwork-management/re-entry',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Re-entry'],
		searchTerms: [
			'reentry paperwork',
			're-entry requirement',
			'request paperwork',
			'upload requirement',
			'paperwork gate',
		],
	},
	{
		id: 'export-student-paperwork-in-bulk',
		title: 'Export student paperwork in bulk',
		guidance:
			'Create a paperwork export for a selected group and review or cancel previous export jobs.',
		href: '/app/paperwork-management/bulk-exporter',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Exports'],
		searchTerms: [
			'bulk paperwork export',
			'download paperwork',
			'export history',
			'cancel export',
			'student group',
		],
	},
	{
		id: 'review-pending-paperwork-approvals',
		title: 'Review pending paperwork approvals',
		guidance:
			'Review submitted student paperwork, program notes, and files before approving, rejecting, or expiring it.',
		href: '/app/paperwork/approve-paperwork',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Approvals'],
		searchTerms: [
			'approve paperwork',
			'reject paperwork',
			'submitted paperwork',
			'pending documents',
			'program notes',
		],
	},
	{
		id: 'review-safe-items-not-invoiced',
		title: 'Review SAFE items not yet invoiced',
		guidance:
			'Edit pending SAFE items, inspect transcripts, or mark eligible items non-billable.',
		href: '/app/safe-billing/not-invoiced',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Billing', 'SAFE'],
		searchTerms: [
			'SAFE billing',
			'not invoiced',
			'non-billable',
			'transcript',
			'bulk nonbillable',
		],
	},
	{
		id: 'approve-safe-student-items',
		title: 'Approve SAFE student items',
		guidance:
			'Upload approval data and progress or regress SAFE items through the student-approval stage.',
		href: '/app/safe-billing/student-approval',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Billing', 'SAFE'],
		searchTerms: [
			'SAFE approval',
			'student approval',
			'approval CSV',
			'progress billing',
			'regress billing',
		],
	},
	{
		id: 'manage-safe-district-funding',
		title: 'Manage SAFE district funding',
		guidance:
			'Edit district-funding information and regress eligible SAFE items when corrections are needed.',
		href: '/app/safe-billing/district-funding',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Billing', 'SAFE'],
		searchTerms: [
			'SAFE funding',
			'district funding',
			'billing correction',
			'regress item',
		],
	},
	{
		id: 'prepare-safe-items-for-invoicing',
		title: 'Prepare SAFE items for invoicing',
		guidance:
			'Review items ready to bill, add selected items to an invoice, or regress items that need correction.',
		href: '/app/safe-billing/ready-to-bill',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Billing', 'SAFE'],
		searchTerms: [
			'ready to bill',
			'add to invoice',
			'bulk invoice',
			'regress billing item',
		],
	},
	{
		id: 'manage-safe-invoices',
		title: 'Manage SAFE invoices',
		guidance:
			'Create or edit invoices, manage entries and adjustments, preview reports, choose recipients and message details, send invoices, or review send history.',
		href: '/app/safe-billing/billing-invoices',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Billing', 'SAFE', 'Invoices'],
		searchTerms: [
			'SAFE invoice',
			'invoice adjustment',
			'invoice preview',
			'send invoice',
			'invoice recipient',
			'invoice email',
			'invoice history',
			'delete invoice',
		],
	},
	{
		id: 'manage-state-tests',
		title: 'Manage state tests',
		guidance:
			'Add, edit, archive, or reinstate tests and review the programs where each test is required or optional.',
		href: '/app/test-manager/test-mapper',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Testing'],
		searchTerms: [
			'test mapper',
			'state test',
			'required test',
			'optional test',
			'archive test',
			'reinstate test',
		],
	},
	{
		id: 'manage-test-events',
		title: 'Manage test events',
		guidance:
			'Create, copy, edit, or archive test events and manage student attendance or registration downloads.',
		href: '/app/test-manager/test-events',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Testing'],
		searchTerms: [
			'testing event',
			'state test event',
			'test attendance',
			'registration list',
			'add students',
			'archive event',
		],
	},
	{
		id: 'manage-survey-periods',
		title: 'Manage survey periods',
		guidance:
			'Create or copy survey periods, configure accountability rules, and manage, upload, download, or finalize participating students.',
		href: '/app/survey-periods-management',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Surveys'],
		searchTerms: [
			'survey window',
			'accountability rules',
			'survey students',
			'survey CSV',
			'finalize students',
			'program types',
		],
	},
	{
		id: 'schedule-and-manage-meetups',
		title: 'Schedule and manage Meetups',
		guidance:
			'Create, copy, edit, finalize, or cancel a Meetup and add eligible attendees.',
		href: '/app/meetup',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Meetups'],
		searchTerms: [
			'add meetup',
			'edit meetup',
			'copy meetup',
			'cancel meetup',
			'finalize meetup',
			'add attendees',
		],
	},
	{
		id: 'record-meetup-attendance',
		title: 'Record Meetup attendance',
		guidance:
			'Import Google Meet attendance or add, correct, excuse, remove, clock, or bulk-mark attendee records.',
		href: '/app/meetup',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Meetups', 'Attendance'],
		searchTerms: [
			'meetup attendance',
			'Google Meet',
			'clock in',
			'clock out',
			'mark absent',
			'gate exemption',
			'excuse attendees',
			'correct attendance time',
			'recover attendance',
		],
	},
	{
		id: 'manage-notification-sets',
		title: 'Manage notification sets',
		guidance:
			'Create, copy, edit, or delete notification sets; configure triggers, conditions, groups, recipients, messages, and variables; or review set history.',
		href: '/app/notification-management/sets',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Notifications'],
		searchTerms: [
			'event sets',
			'notification template',
			'automated student message',
			'notification trigger',
			'recipient groups',
			'approval message',
			'message variable',
			'copy notification',
			'notification history',
		],
	},
	{
		id: 'manage-default-notification-subscriptions',
		title: 'Manage default notification subscriptions',
		guidance:
			'Filter defaults by role, program, or notification set, then choose not subscribed, subscribed, or required for each role.',
		href: '/app/notification-management/subscription-levels',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Notifications'],
		searchTerms: [
			'subscription levels',
			'notification defaults',
			'default subscription',
			'disable notifications',
			'required notification',
			'role notification settings',
			'program notification settings',
		],
	},
	{
		id: 'review-notification-update-history',
		title: 'Review notification update history',
		guidance:
			'Find notification configuration changes and open an update-history record for details.',
		href: '/app/notification-management/update-history',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Notifications', 'History'],
		searchTerms: [
			'notification changes',
			'update audit',
			'notification audit log',
			'configuration history',
		],
	},
	{
		id: 'review-notification-event-history',
		title: 'Review notification event history',
		guidance:
			'Find generated notifications by set name, recipient ID, associated user ID, date, or subject, then inspect event details or the source notification set.',
		href: '/app/notification-management/event-history',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Administration', 'Notifications', 'History'],
		searchTerms: [
			'notification events',
			'event log',
			'sent notification',
			'delivery history',
			'event details',
			'notification recipient',
			'recipient ID',
			'associated user ID',
			'notification subject',
			'source notification set',
		],
	},
	{
		id: 'run-enrollment-report',
		title: 'Run the enrollment report',
		guidance:
			'Review program enrollment data, inspect or print enrollment rules, and open available student details.',
		href: '/app/enrollment-report',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Enrollment'],
		searchTerms: [
			'enrollment report',
			'program enrollment',
			'enrollment rules',
			'print rules',
			'report inquiry',
		],
	},
	{
		id: 'open-program-dashboard',
		title: 'Open the program dashboard',
		guidance:
			'View program metrics and launch enrollment, graduation, class, credit, Meetup, withdrawal, pause, and approval reports.',
		href: '/dashboard/program',
		application: 'dashboard',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Program Dashboard'],
		searchTerms: [
			'admin dashboard',
			'enrollment metrics',
			'graduates',
			'counted classes',
			'credit earned rate',
			'meetup attendance',
			'withdrawals',
			'pauses',
		],
	},
	{
		id: 'browse-academy-reports',
		title: 'Browse Academy reports',
		guidance:
			'Open the Academy reporting area to find attendance and other reports available to your role.',
		href: '/ga/reporting/academy',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Academy'],
		searchTerms: [
			'academy reports',
			'attendance finalized',
			'held attendance',
			'attendance report',
			'reporting',
		],
	},
	{
		id: 'approve-written-learning-plans',
		title: 'Approve written learning plans',
		guidance: 'Review written student learning plans waiting for approval.',
		href: '/ga/learning-plan/written/approval',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Learning Plans', 'Approvals'],
		searchTerms: [
			'WSLP approvals',
			'written learning plan',
			'approve WSLP',
			'learning plan approval',
		],
	},
	{
		id: 'open-portal-quick-reference',
		title: 'Open the portal quick-reference guide',
		guidance:
			'Open the program-scoped Graduation Alliance portal guide in the external help site.',
		href: '/ga/institute/quick-reference-redirect',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Help', 'Portal Guide'],
		searchTerms: [
			'quick reference',
			'portal guide',
			'help guide',
			'program help',
			'Graduation Alliance guide',
		],
	},
	{
		id: 'review-active-student-pauses',
		title: 'Review active student pauses',
		guidance:
			'Find currently paused students by program, date, or reason, review work-queue assignments, and open Student Activity.',
		href: '/app/trackers/registrar/active-pauses',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Exit Requests', 'Active Pauses'],
		searchTerms: [
			'active pauses',
			'paused students',
			'pause dates',
			'pause reason',
			'pause tracker',
		],
	},
	{
		id: 'review-student-update-history',
		title: 'Review student update history',
		guidance:
			'Review completed student data-change requests, compare previous and approved values, and open available proof or change history.',
		href: '/app/trackers/student_update/history',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Data Updates', 'History'],
		searchTerms: [
			'student data change history',
			'approved student updates',
			'rejected student updates',
			'previous value',
			'approved value',
			'change proof',
		],
	},
	{
		id: 'find-and-run-reports',
		title: 'Find and run a report',
		guidance:
			'Choose an available report, set its filters, view the results, and export CSV output when the report supports it.',
		href: '/ga/reports',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Reports', 'Report Manager'],
		searchTerms: [
			'report manager',
			'custom report',
			'export portal data',
			'spreadsheet',
			'CSV',
			'student progress report',
			'attendance report',
			'enrollment by tier',
			'multi period enrollment',
			'pause request history',
			'student demographics',
			'course activity audit',
			'graded activity',
			'program region',
			'Assignment Time Credit Exceptions',
			'assignments submitted under a syllabus-minute rule that do not have a direct section time-credit mapping',
			'missing time credit mapping',
		],
	},
	{
		id: 'manage-scheduled-reports',
		title: 'Manage scheduled reports',
		guidance:
			'Review report subscriptions, including attendance-report delivery, and, when permitted, create, edit, or delete schedules with their frequency, filters, and email recipients.',
		href: '/ga/reports/scheduled',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Reports', 'Scheduled Reports'],
		searchTerms: [
			'schedule report',
			'scheduled report delivery',
			'report email recipient',
			'report subscription',
			'report frequency',
			'attendance report schedule',
			'restore report delivery',
			'group progress report',
			'scoped report',
			'Assignment Time Credit Exceptions',
			'assignments submitted under a syllabus-minute rule that do not have a direct section time-credit mapping',
			'time credit exception schedule',
		],
	},
	{
		id: 'run-truancy-report',
		title: 'Run the truancy report',
		guidance:
			'Open the charter truancy report and choose the reporting date or student detail you need.',
		href: '/ga/attendance/truancy/report',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Attendance', 'Truancy'],
		searchTerms: [
			'charter report',
			'truancy report',
			'student truancy',
			'attendance compliance',
		],
	},
	{
		id: 'generate-finalized-attendance-reports',
		title: 'Generate finalized attendance reports',
		guidance:
			'Generate finalized attendance workbooks for an available reporting profile, then download the spreadsheet or request delivery by email.',
		href: '/ga/attendance/reports/finalized',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Attendance', 'Finalized'],
		searchTerms: [
			'finalized attendance',
			'attendance workbook',
			'Generate & Email Spreadsheet',
			'current attendance report',
			'academy attendance',
		],
	},
	{
		id: 'review-held-attendance-reports',
		title: 'Review held attendance reports',
		guidance:
			'Review held attendance-report runs; open a run to download, release, reject, or regenerate eligible deliveries.',
		href: '/ga/attendance/reports/review',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Reports', 'Attendance', 'Delivery Review'],
		searchTerms: [
			'held attendance',
			'approve attendance report',
			'report delivery',
			'attendance report run',
		],
	},
	{
		id: 'run-checklist-reports',
		title: 'Run checklist reports',
		guidance:
			'Open checklist reporting to review checklist completion and related user checklist data available to your role.',
		href: '/ga/checklist/reports',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Checklists'],
		searchTerms: [
			'checklist report',
			'user checklist',
			'checklist completion',
			'charter report',
		],
	},
	{
		id: 'open-nwea-reports',
		title: 'Open NWEA reports',
		guidance:
			'Open the NWEA reporting dashboard to review the NWEA data available to your role.',
		href: '/ga/nwea',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Assessments', 'NWEA'],
		searchTerms: [
			'NWEA dashboard',
			'NWEA report',
			'assessment report',
			'charter report',
		],
	},
	{
		id: 'run-dr-eligibility-report',
		title: 'Run the DR eligibility report',
		guidance:
			'Open the DR eligibility report to review eligibility results available for your programs.',
		href: '/ga/reporting/dr_eligibility',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Eligibility'],
		searchTerms: ['DR eligibility', 'eligibility report', 'charter report'],
	},
	{
		id: 'review-teacher-time-tracking',
		title: 'Review teacher time tracking',
		guidance:
			'Open the teacher time-tracker report to review reported time for eligible staff and programs.',
		href: '/ga/time-tracker/report/teacher',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Reports', 'Staff', 'Time Tracking'],
		searchTerms: [
			'teacher time tracker',
			'staff time report',
			'teacher hours',
			'time tracking report',
		],
	},
	{
		id: 'run-charter-learning-opportunities-report',
		title: 'Run the charter learning-opportunities report',
		guidance:
			'Calculate learning-opportunity data for eligible charter programs.',
		href: '/ga/reports/oh_charter_learning_opportunities',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Reports', 'Charter', 'Learning Opportunities'],
		searchTerms: [
			'learning opportunities',
			'charter learning report',
			'Ohio charter',
			'offline learning',
		],
	},
	{
		id: 'manage-points-orders',
		title: 'Manage student points orders',
		guidance:
			'Review pending, completed, cancelled, or fulfilled points orders; fulfill or cancel eligible orders and export the list to CSV.',
		href: '/ga/points/order-list',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Students', 'Points Orders'],
		searchTerms: [
			'points store orders',
			'student rewards',
			'fulfill order',
			'cancel order',
			'order list',
			'points order CSV',
		],
	},
	{
		id: 'remove-course-discussion-posts',
		title: 'Remove old course discussion posts',
		guidance:
			'Select a course, section, and assignments to queue deletion of discussion posts older than the configured retention threshold.',
		href: '/ga/course/assignment_discussion_posts_delete',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Course Discussions'],
		searchTerms: [
			'delete discussion posts',
			'course discussion cleanup',
			'assignment comments',
			'old discussion posts',
			'LMS discussion',
		],
	},
] as const satisfies readonly SitemapEntry[];
